/**
 * Config JSON Viewer Component
 *
 * Displays global app configuration documents with:
 * - Grouping by document type
 * - Collapsible sections
 * - Syntax-highlighted JSON
 * - Filter by document type
 * - Copy to clipboard functionality
 */

import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Copy, Check, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { GlobalConfigDocument, GroupedDocuments } from '@/types/global-config';

interface ConfigJsonViewerProps {
  /** Array of configuration documents */
  documents: GlobalConfigDocument[];
  /** Total count of documents */
  count: number;
}

/**
 * Group documents by type
 */
function groupDocumentsByType(documents: GlobalConfigDocument[]): GroupedDocuments[] {
  const grouped = documents.reduce(
    (acc, doc) => {
      const type = doc.type || 'unknown';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    },
    {} as Record<string, GlobalConfigDocument[]>
  );

  return Object.entries(grouped)
    .map(([type, docs]) => ({
      type,
      documents: docs,
      count: docs.length,
    }))
    .sort((a, b) => a.type.localeCompare(b.type));
}

/**
 * Collapsible Document Group Component
 */
function DocumentGroup({
  group,
  isExpanded,
  onToggle,
}: {
  group: GroupedDocuments;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopyGroup = async () => {
    try {
      const json = JSON.stringify(group.documents, null, 2);
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error: unknown) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <FileJson className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{group.type}</span>
          <Badge variant="secondary" className="text-xs">
            {group.count} {group.count === 1 ? 'document' : 'documents'}
          </Badge>
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 bg-muted/10 border-t">
          <div className="flex justify-end mb-2">
            <Button variant="ghost" size="sm" onClick={handleCopyGroup} className="h-7 text-xs">
              {copied ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy JSON
                </>
              )}
            </Button>
          </div>
          <div className="space-y-3">
            {group.documents.map((doc, index) => (
              <div
                key={doc.id || index}
                className="bg-background rounded border p-3 overflow-hidden"
              >
                <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words">
                  <code className="text-muted-foreground">{JSON.stringify(doc, null, 2)}</code>
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Config JSON Viewer Component
 */
export function ConfigJsonViewer({ documents, count }: ConfigJsonViewerProps) {
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>('all');
  const [copiedAll, setCopiedAll] = useState(false);

  // Group documents by type
  const groupedDocuments = useMemo(() => groupDocumentsByType(documents), [documents]);

  // Get unique types for filter dropdown
  const documentTypes = useMemo(
    () => ['all', ...groupedDocuments.map(g => g.type)],
    [groupedDocuments]
  );

  // Filter groups based on selected type
  const filteredGroups = useMemo(() => {
    if (filterType === 'all') {
      return groupedDocuments;
    }
    return groupedDocuments.filter(g => g.type === filterType);
  }, [groupedDocuments, filterType]);

  const toggleGroup = (type: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedTypes(new Set(filteredGroups.map(g => g.type)));
  };

  const collapseAll = () => {
    setExpandedTypes(new Set());
  };

  const handleCopyAll = async () => {
    try {
      const json = JSON.stringify(documents, null, 2);
      await navigator.clipboard.writeText(json);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (error: unknown) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FileJson className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-sm">No configuration documents found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with stats and controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileJson className="h-4 w-4" />
          <span>
            {count} {count === 1 ? 'document' : 'documents'} in {groupedDocuments.length}{' '}
            {groupedDocuments.length === 1 ? 'type' : 'types'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter dropdown */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type === 'all' ? 'All types' : type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Expand/Collapse buttons */}
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>

          {/* Copy all button */}
          <Button variant="outline" size="sm" onClick={handleCopyAll}>
            {copiedAll ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy All
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Document groups */}
      <div className="space-y-3">
        {filteredGroups.map(group => (
          <DocumentGroup
            key={group.type}
            group={group}
            isExpanded={expandedTypes.has(group.type)}
            onToggle={() => toggleGroup(group.type)}
          />
        ))}
      </div>

      {/* Empty filter result */}
      {filteredGroups.length === 0 && filterType !== 'all' && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No documents found for type "{filterType}"</p>
          <Button variant="link" size="sm" onClick={() => setFilterType('all')} className="mt-2">
            Show all types
          </Button>
        </div>
      )}
    </div>
  );
}
